/**
 * Button (shadcn/ui pattern on Radix Slot, TZ 8.13; owner design pass of 2026-10-01). One style system for every
 * button of both panels:
 * - primary: solid brand action colour, white label, radius 8 px, 36 px (md) / 40 px (lg), medium weight;
 * - secondary: card background, 1 px neutral control border, dark label, subtle hover;
 * - ghost / link: no frame (toolbars, inline actions); danger: solid red for destructive confirmations.
 * `loading` shows a spinner, disables the button and sets aria-busy. Icon buttons need an aria-label.
 */
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2Icon } from 'lucide-react';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';
import { classNames } from '../styling/class_names';

export const buttonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)] disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-action text-action-ink shadow-xs hover:bg-action-hover',
        secondary: 'border border-control-border bg-card text-ink shadow-xs hover:bg-control-hover',
        ghost: 'text-ink hover:bg-control-hover',
        danger: 'bg-danger-action text-white shadow-xs hover:bg-danger-action-hover',
        link: 'h-auto px-0 text-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-5 text-base',
        icon: 'size-9',
        iconSmall: 'size-8',
      },
    },
    compoundVariants: [{ variant: 'link', className: 'h-auto px-0' }],
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  /** Render the child element (e.g. a router link) with button styling. */
  asChild?: boolean;
  /** Pending action: spinner before the label, disabled, aria-busy. */
  loading?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  type,
  disabled,
  children,
  ...buttonProps
}: ButtonProps) {
  const variantClasses = classNames(buttonVariants({ variant, size }), className);
  if (asChild) {
    return (
      <Slot.Root className={variantClasses} {...buttonProps}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      className={variantClasses}
      type={type ?? 'button'}
      disabled={disabled === true || loading}
      aria-busy={loading ? true : undefined}
      {...buttonProps}
    >
      {loading ? <Loader2Icon className="animate-spin" aria-hidden /> : null}
      {children}
    </button>
  );
}
